/**
 * Mensajes automáticos a pacientes: recordatorio y confirmación de citas, y felicitación de cumpleaños
 * una semana antes, con control de cortesía y descuento. Plantillas editables por clínica.
 */
import { aInstante, aLocal, fechaLarga, horaLarga, sumarDias } from "./agenda.ts";
import { norm } from "./texto.ts";

export const TIPOS_MENSAJE = ["recordatorio", "cumpleanos", "confirmacion", "control_implante", "control_ortodoncia", "control_alineadores", "club_renovacion"] as const;
export type TipoMensaje = (typeof TIPOS_MENSAJE)[number];

/**
 * Controles periódicos: avisos que la clínica activa a su gusto. Cada uno cuenta los meses desde un hecho distinto:
 *  - profilaxis: desde la última profilaxis realizada (PRO o PRS en Plan y pagos).
 *  - sin_visita: desde la última cita atendida, para el paciente que dejó de venir.
 *  - atm: desde la última placa oclusal o tratamiento de ATM realizado (código PLO o su nombre).
 *  - periodoncia: desde el último raspado o mantenimiento periodontal realizado (RAS o MPE).
 * Los `puntuales` se avisan UNA sola vez por cada hecho, cuando se cumple su plazo (y hasta 14 días después, para no avisar de lo muy antiguo):
 *  - cirugia_7d y cirugia_1m: a la semana y al mes de una cirugía (implantes, cirugía oral o un procedimiento quirúrgico realizado).
 *  - endodoncia_6m y endodoncia_12m: a los 6 y a los 12 meses de terminar una endodoncia.
 */
export const REGLAS_CONTROL = ["profilaxis", "sin_visita", "atm", "periodoncia", "cirugia_7d", "cirugia_1m", "endodoncia_6m", "endodoncia_12m"] as const;
export type ReglaControl = (typeof REGLAS_CONTROL)[number];
export type ClavePlantilla = TipoMensaje | `control_${ReglaControl}`;
export const CLAVES_PLANTILLA: readonly string[] = [...TIPOS_MENSAJE, ...REGLAS_CONTROL.map((r) => `control_${r}`)];
export const NOMBRE_CONTROL: Record<ReglaControl, string> = {
  profilaxis: "Limpieza dental (profilaxis)", sin_visita: "Paciente que dejó de venir", atm: "Placa oclusal y ATM", periodoncia: "Mantenimiento periodontal",
  cirugia_7d: "Control de cirugía a los 7 días", cirugia_1m: "Control de cirugía al mes", endodoncia_6m: "Control de endodoncia a los 6 meses", endodoncia_12m: "Control de endodoncia al año",
};
/** Controles de una sola vez por hecho: dan la fecha en que toca avisar a partir de la fecha del hecho (cirugía o endodoncia terminada). */
export const CONTROL_PUNTUAL: Partial<Record<ReglaControl, (desde: string) => string>> = {
  cirugia_7d: (d) => sumarDias(d, 7), cirugia_1m: (d) => sumarMeses(d, 1), endodoncia_6m: (d) => sumarMeses(d, 6), endodoncia_12m: (d) => sumarMeses(d, 12),
};
/** Días después de la fecha que toca en que aún se avisa un control puntual (si la regla se activa tarde, no se avisa de lo muy antiguo). */
export const DIAS_VENTANA_PUNTUAL = 14;
/** Frase corta para la plantilla de WhatsApp Business (variable 3): «…te recordamos {motivo}». */
export const MOTIVO_CONTROL: Record<ReglaControl, string> = {
  profilaxis: "que toca tu limpieza dental (profilaxis)",
  sin_visita: "que hace tiempo no nos visitas y te toca una revisión",
  atm: "que toca el control de tu placa oclusal y de tu ATM",
  periodoncia: "que toca tu mantenimiento periodontal (control y limpieza de encías)",
  cirugia_7d: "que ya pasó una semana desde tu cirugía y toca tu control",
  cirugia_1m: "que ya pasó un mes desde tu cirugía y toca revisar tu cicatrización",
  endodoncia_6m: "que toca el control de tu endodoncia a los 6 meses",
  endodoncia_12m: "que toca el control de tu endodoncia al año",
};
export interface AjusteControl { activo: boolean; meses: number }
/** Tope de avisos nuevos por día y por regla: al activar una regla por primera vez, los pacientes antiguos se avisan poco a poco. */
export const MAX_CONTROLES_DIA = 25;
export const CANALES = ["whatsapp", "email"] as const;
export type Canal = (typeof CANALES)[number];

export interface ConfigMensajes {
  activo: boolean;
  canales: Canal[];                // en orden de preferencia
  recordatorioHoras: number;       // cuántas horas antes de la cita
  horaEnvio: string;               // hora local para mensajes de cumpleaños ("09:00")
  cumpleanosDiasAntes: number;
  descuentoCumpleanos: number;     // %
  vigenciaCumpleanosDias: number;
  /** Aviso automático para el control de los implantes ya colocados. */
  controlImplanteActivo: boolean;
  controlImplanteMeses: number;    // cada cuántos meses
  /** Aviso automático del control de ortodoncia a los pacientes con tratamiento activo. */
  controlOrtodonciaActivo: boolean;
  controlOrtodonciaMeses: number;  // cada cuántos meses (por defecto, uno)
  /** Con alineadores el control se avisa cada 10, 15, 21 o 30 días (cada tratamiento puede elegir el suyo). */
  controlAlineadoresDias: number;
  /** Controles periódicos (apagados por defecto: la clínica los activa). */
  controles: Record<ReglaControl, AjusteControl>;
  plantillas: Record<ClavePlantilla, string>;
}

export const CONFIG_MENSAJES_BASE: ConfigMensajes = {
  activo: true,
  canales: ["whatsapp", "email"],
  recordatorioHoras: 24,
  horaEnvio: "09:00",
  cumpleanosDiasAntes: 7,
  descuentoCumpleanos: 10,
  vigenciaCumpleanosDias: 30,
  controlImplanteActivo: true,
  controlImplanteMeses: 4,
  controlOrtodonciaActivo: true,
  controlOrtodonciaMeses: 1,
  controlAlineadoresDias: 30,
  controles: {
    profilaxis: { activo: false, meses: 6 }, sin_visita: { activo: false, meses: 12 }, atm: { activo: false, meses: 6 }, periodoncia: { activo: false, meses: 3 },
    cirugia_7d: { activo: false, meses: 1 }, cirugia_1m: { activo: false, meses: 1 }, endodoncia_6m: { activo: false, meses: 6 }, endodoncia_12m: { activo: false, meses: 12 },
  },
  plantillas: {
    control_alineadores: "Hola {nombre} 😁 En {clinica} te recordamos tu control de alineadores: toca revisar tu avance y entregarte tus próximos alineadores. Cumplir los controles a tiempo mantiene tu tratamiento en el plazo previsto. Responde a este mensaje para agendar tu cita.",
    control_cirugia_7d: "Hola {nombre} 🦷 En {clinica} queremos saber cómo sigues después de tu cirugía: ya pasó una semana y es un buen momento para tu control. Si tienes dolor, inflamación o sangrado que no cede, escríbenos de inmediato. Escríbenos para agendar tu control.",
    control_cirugia_1m: "Hola {nombre} 😁 En {clinica} ya pasó un mes desde tu cirugía. Queremos revisar tu cicatrización y cómo te sientes. Escríbenos para agendar tu control.",
    control_endodoncia_6m: "Hola {nombre} 🦷 En {clinica} te recordamos el control de tu endodoncia a los 6 meses. Revisar la pieza con una radiografía permite confirmar que todo sana bien. Escríbenos para agendar tu cita.",
    control_endodoncia_12m: "Hola {nombre} 🦷 En {clinica} te recordamos el control de tu endodoncia al año. Es la revisión que confirma que la pieza se mantiene sana. Escríbenos para agendar tu cita.",
    control_profilaxis: "Hola {nombre} 🦷 En {clinica} te recordamos que ya toca tu limpieza dental (profilaxis): se recomienda cada {meses} meses. Prevenir sarro y caries a tiempo evita tratamientos más largos. Escríbenos para agendar tu cita.",
    control_sin_visita: "Hola {nombre} 😁 En {clinica} hace tiempo que no te vemos. Una revisión periódica permite detectar a tiempo cualquier problema y cuidar tu sonrisa. Escríbenos para agendar tu control.",
    control_atm: "Hola {nombre} 😁 En {clinica} te recordamos el control de tu placa oclusal y de tu articulación (ATM). Revisar el ajuste de la placa y cuidar la articulación ayuda a evitar dolor y desgaste. Escríbenos para agendar tu cita.",
    control_periodoncia: "Hola {nombre} 🦷 En {clinica} te recordamos que toca tu mantenimiento periodontal (control y limpieza de encías), recomendado cada {meses} meses. Es lo que mantiene tus encías sanas después del tratamiento. Escríbenos para agendar tu cita.",
    recordatorio: "Hola {nombre} 👋 Te recordamos tu cita en {clinica} el {fecha} a las {hora} con {profesional}.\nConfirma o reprograma aquí: {enlace}\nSi prefieres, responde SÍ para confirmar o NO para cancelar.",
    control_implante: "Hola {nombre} 🦷 En {clinica} te recordamos que toca el control de tu implante dental (se recomienda cada {meses} meses). El control periódico permite detectar a tiempo cualquier problema y cuidar tu implante por muchos años. Escríbenos para agendar tu cita.",
    control_ortodoncia: "Hola {nombre} 😁 En {clinica} te recordamos que ya toca tu control mensual de ortodoncia. Asistir cada mes mantiene tu tratamiento en el tiempo previsto y evita que se atrasen los resultados. Responde a este mensaje para agendar tu cita.",
    club_renovacion: "Hola {nombre} 😁 Tu {club} en {clinica} vence el {fecha}. {cupos} Renuévalo para seguir disfrutando tus beneficios: escríbenos o pasa por la clínica.",
    confirmacion: "¡Gracias, {nombre}! Tu cita del {fecha} a las {hora} en {clinica} quedó confirmada. Te esperamos.",
    cumpleanos: "¡Feliz cumpleaños por adelantado, {nombre}! 🎉 En {clinica} queremos celebrarte: te regalamos un control odontológico de cortesía y {descuento} % de descuento en tus tratamientos hasta el {vigencia}. Responde a este mensaje para agendar tu cita.",
  },
};

/** Mezcla la configuración guardada con la base (lo que falte toma el valor por defecto). */
export function configMensajes(guardada: unknown): ConfigMensajes {
  const g = (guardada && typeof guardada === "object" ? guardada : {}) as Partial<ConfigMensajes>;
  const b = CONFIG_MENSAJES_BASE;
  const num = (v: unknown, d: number, min: number, max: number) => (typeof v === "number" && v >= min && v <= max ? v : d);
  return {
    activo: typeof g.activo === "boolean" ? g.activo : b.activo,
    canales: Array.isArray(g.canales) && g.canales.every((c) => (CANALES as readonly string[]).includes(c)) && g.canales.length ? g.canales : b.canales,
    recordatorioHoras: num(g.recordatorioHoras, b.recordatorioHoras, 1, 72),
    horaEnvio: typeof g.horaEnvio === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(g.horaEnvio) ? g.horaEnvio : b.horaEnvio,
    cumpleanosDiasAntes: num(g.cumpleanosDiasAntes, b.cumpleanosDiasAntes, 0, 30),
    descuentoCumpleanos: num(g.descuentoCumpleanos, b.descuentoCumpleanos, 0, 100),
    vigenciaCumpleanosDias: num(g.vigenciaCumpleanosDias, b.vigenciaCumpleanosDias, 1, 120),
    controlImplanteActivo: typeof g.controlImplanteActivo === "boolean" ? g.controlImplanteActivo : b.controlImplanteActivo,
    controlImplanteMeses: num(g.controlImplanteMeses, b.controlImplanteMeses, 1, 24),
    controlOrtodonciaActivo: typeof g.controlOrtodonciaActivo === "boolean" ? g.controlOrtodonciaActivo : b.controlOrtodonciaActivo,
    controlOrtodonciaMeses: num(g.controlOrtodonciaMeses, b.controlOrtodonciaMeses, 1, 6),
    controlAlineadoresDias: [10, 15, 21, 30].includes(Number(g.controlAlineadoresDias)) ? Number(g.controlAlineadoresDias) : b.controlAlineadoresDias,
    controles: Object.fromEntries(REGLAS_CONTROL.map((r) => {
      const x = (g.controles?.[r] ?? {}) as Partial<AjusteControl>;
      return [r, { activo: typeof x.activo === "boolean" ? x.activo : b.controles[r].activo, meses: num(x.meses, b.controles[r].meses, 1, 36) }];
    })) as Record<ReglaControl, AjusteControl>,
    plantillas: { ...b.plantillas, ...Object.fromEntries(Object.entries(g.plantillas ?? {}).filter(([k, v]) => CLAVES_PLANTILLA.includes(k) && typeof v === "string" && v.trim())) },
  };
}

export const VARIABLES = ["{nombre}", "{clinica}", "{fecha}", "{hora}", "{profesional}", "{enlace}", "{descuento}", "{vigencia}", "{meses}", "{club}", "{cupos}"];

/** Reemplaza {variables}; las desconocidas quedan como están para que se note el error en la vista previa. */
export function llenarPlantilla(texto: string, v: Record<string, string>): string {
  return texto.replace(/\{(\w+)\}/g, (m, k: string) => (k in v ? v[k] : m));
}

/** Primer nombre para un saludo cercano ("María Fernanda" → "María Fernanda"; "JOSÉ LUIS" → "José Luis"). */
export function nombreSaludo(nombres: string): string {
  return nombres.trim().toLowerCase().replace(/(^|\s)\p{L}/gu, (c) => c.toUpperCase());
}

/**
 * Cuándo enviar el recordatorio: X horas antes. Si la cita se agendó con menos anticipación,
 * se envía en cuanto se pueda, siempre que falten al menos 2 horas; si no, no se envía.
 */
export function momentoRecordatorio(inicioCita: Date, horasAntes: number, ahora: Date): Date | null {
  const ideal = new Date(inicioCita.getTime() - horasAntes * 3600000);
  if (ideal > ahora) return ideal;
  return inicioCita.getTime() - ahora.getTime() >= 2 * 3600000 ? ahora : null;
}

/** ¿El cumpleaños cae exactamente `diasAntes` días después de `hoy`? (el 29 de febrero se celebra el 28 en años no bisiestos) */
export function cumpleEnDias(nacimiento: string, hoy: string, diasAntes: number): { cae: boolean; fecha: string } {
  const objetivo = sumarDias(hoy, diasAntes);
  const [anio] = objetivo.split("-").map(Number);
  let md = nacimiento.slice(5);
  const bisiesto = (anio % 4 === 0 && anio % 100 !== 0) || anio % 400 === 0;
  if (md === "02-29" && !bisiesto) md = "02-28";
  const fecha = `${anio}-${md}`;
  return { cae: fecha === objetivo, fecha };
}

/** Suma meses a una fecha ISO; si el día no existe en el mes de destino queda el último día del mes (31 de octubre + 4 meses = 28 de febrero). */
export function sumarMeses(fecha: string, n: number): string {
  const [a, m, d] = fecha.split("-").map(Number);
  const total = a * 12 + (m - 1) + n, anio = Math.floor(total / 12), mes = (total % 12) + 1;
  const ultimo = new Date(Date.UTC(anio, mes, 0)).getUTCDate();
  return `${anio}-${String(mes).padStart(2, "0")}-${String(Math.min(d, ultimo)).padStart(2, "0")}`;
}

/**
 * ¿Toca avisar el control del implante? `base` es la fecha más reciente entre la cirugía, el último control y el último aviso:
 * desde ahí se cuentan `meses`. Así, quien viene a su control reinicia la cuenta, y quien no, recibe un aviso cada `meses`.
 */
export const tocaControlImplante = (base: string, hoy: string, meses: number) => sumarMeses(base, meses) <= hoy;

/** ¿Toca avisar el control de ortodoncia? Igual que el de implantes: `base` es la fecha más reciente entre el inicio del tratamiento, el último control y el último aviso. */
export const tocaControlOrtodoncia = tocaControlImplante;

/** Hora local de envío del día `fecha` como instante. */
export const instanteEnvio = (fecha: string, hora: string) => aInstante(fecha, hora);

/** Interpreta la respuesta del paciente por WhatsApp. */
export function interpretarRespuesta(texto: string): "confirmar" | "cancelar" | null {
  const t = norm(texto).replace(/[^\p{L}\p{N}\s]/gu, " ").trim();
  if (/^(si|sí|1|confirmo|confirmado|ok|okay|dale|claro|de acuerdo|si confirmo|asistire|ahi estare)\b/.test(t)) return "confirmar";
  if (/^(no|2|cancelo|cancelar|no puedo|no podre|no asistire)\b/.test(t)) return "cancelar";
  return null;
}

export interface DatosCita { inicio: Date; paciente: string; clinica: string; profesional: string; enlace: string }
/** Sin enlace (Donna aún no está publicada en internet), se quita la línea que lo menciona y el paciente responde SÍ o NO. */
export function sinLineaDeEnlace(plantilla: string): string {
  return plantilla.split("\n").filter((l) => !l.includes("{enlace}")).join("\n").trim();
}
export function textoCita(plantilla: string, d: DatosCita): string {
  const l = aLocal(d.inicio);
  if (!d.enlace) plantilla = sinLineaDeEnlace(plantilla);
  return llenarPlantilla(plantilla, { nombre: nombreSaludo(d.paciente), clinica: d.clinica, fecha: fechaLarga(l.fecha), hora: horaLarga(l.hora), profesional: d.profesional, enlace: d.enlace });
}
export function textoCumpleanos(plantilla: string, nombre: string, clinica: string, descuento: number, vigencia: string): string {
  const d = new Date(vigencia + "T12:00:00Z");
  const v = d.toLocaleDateString("es-EC", { day: "numeric", month: "long", timeZone: "UTC" });
  return llenarPlantilla(plantilla, { nombre: nombreSaludo(nombre), clinica, descuento: String(descuento), vigencia: v });
}

/** Enlace de WhatsApp con el mensaje escrito (para enviar a mano, sin API). */
export function enlaceWhatsApp(celularInternacional: string, texto: string): string {
  return `https://wa.me/${celularInternacional}?text=${encodeURIComponent(texto)}`;
}

/**
 * ¿El paciente puede abrir este enlace desde su celular? No, si Donna corre solo en la computadora de la
 * clínica (localhost o una dirección de la red interna): entonces el mensaje va sin enlace.
 */
export function urlAccesibleDesdeInternet(url: string): boolean {
  try {
    const h = new URL(url).hostname.toLowerCase();
    if (h === "localhost" || h.endsWith(".local") || h.endsWith(".localhost") || !h.includes(".")) return false;
    if (/^(127\.|10\.|192\.168\.|169\.254\.|0\.)/.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h)) return false;
    return true;
  } catch { return false; }
}

export interface ProximoCumple<T> { paciente: T; fecha: string; enDias: number; cumple: number | null }

/** Pacientes que cumplen años entre hoy y `dias` días después (el 29 de febrero se celebra el 28 en años no bisiestos). */
export function proximosCumpleanos<T extends { nacimiento: string | null }>(pacientes: T[], hoy: string, dias: number): ProximoCumple<T>[] {
  const out: ProximoCumple<T>[] = [];
  const [ha] = hoy.split("-").map(Number);
  const dia = 86400000, t0 = Date.parse(hoy + "T00:00:00Z");
  for (const p of pacientes) {
    if (!p.nacimiento || !/^\d{4}-\d{2}-\d{2}$/.test(p.nacimiento)) continue;
    const [na] = p.nacimiento.split("-").map(Number);
    for (const anio of [ha, ha + 1]) {
      let md = p.nacimiento.slice(5);
      const bisiesto = (anio % 4 === 0 && anio % 100 !== 0) || anio % 400 === 0;
      if (md === "02-29" && !bisiesto) md = "02-28";
      const fecha = `${anio}-${md}`;
      const enDias = Math.round((Date.parse(fecha + "T00:00:00Z") - t0) / dia);
      if (enDias >= 0 && enDias <= dias) { out.push({ paciente: p, fecha, enDias, cumple: anio - na > 0 && anio - na < 130 ? anio - na : null }); break; }
    }
  }
  return out.sort((a, b) => a.enDias - b.enDias);
}
